import { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import { dependsOn } from '@lib/customization/poiDesign';
import { STEP_STATUS_LABELS, type Draft } from '@lib/customization/poiDraft';
import {
  evaluateField, evaluateGroup, ruleContext, STEP_STATUS_ID, stepStatuses, type PoiRuleValue,
} from '@lib/customization/poiRules';
import { useLocalization } from '@lib/localization';
import { type Inspection } from '@lib/spproject/types';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { type PoiField, type ResolvedItem } from '@store/api/poiTypeEndpoints';
import { useCallback, useState } from 'react';
import { RedoIcon } from 'react-line-awesome';
import { deviceStyles, LOGIC_COLOR, segmentedStyles } from './designer.styles';
import { whenWords } from './describe';
import FieldInput from './FieldInput';
import { LabelText, type Languages } from './FieldHeading';

interface Props {
  draft: Draft;
  inherited: ResolvedItem[];
  candidates: ReadonlyMap<string, RuleCandidate>;
  languages: Languages;
}

/**
 * What a device starts the form holding: each field's default, and a range at its minimum,
 * which is what a device records for a slider nobody moved.
 */
const startingAnswers = (items: ResolvedItem[]): Record<string, PoiRuleValue> => Object.fromEntries(items
  .flatMap((item) => (item.kind === 'field' ? [item.field] : item.group.fields))
  .flatMap((field) => (field.id ? [[field.id, field.value ?? (field.type === 'Range' ? field.min ?? 0 : undefined)]] : [])));

/**
 * The form as the device shows it to an inspector: only what the current answers make
 * visible, through the same evaluator the rest of Insight uses. A field an answer brought in
 * carries a small dot, with the reason on hover.
 *
 * The step's status is the inspector's to give on the device, above the form. Here it is a
 * control of the preview, and only for a form with a rule that asks about it: a type without
 * one tries exactly as it did.
 */
const TryForm: React.FC<Props> = ({ draft, inherited, candidates, languages }) => {
  const { t } = useLocalization();

  // The order `/effective` resolves in: ancestors first, then the type's fields, then its groups.
  const items: ResolvedItem[] = [
    ...inherited,
    ...draft.fields.map((field): ResolvedItem => ({ kind: 'field', field, inheritedFrom: null })),
    ...draft.groups.map((group): ResolvedItem => ({ kind: 'group', group, inheritedFrom: null })),
  ];

  const [answers, setAnswers] = useState(() => startingAnswers(items));
  const [status, setStatus] = useState<Inspection.NoticeType>('NotChecked');
  const answer = useCallback((id: string, value: PoiRuleValue) => setAnswers((current) => ({ ...current, [id]: value })), []);
  const context = ruleContext(items, answers, status);

  const asksStatus = items
    .flatMap((item) => (item.kind === 'field' ? [item.field] : [item.group, ...item.group.fields]))
    .some((entry) => dependsOn(entry).includes(STEP_STATUS_ID));

  const reset = () => {
    setAnswers(startingAnswers(items));
    setStatus('NotChecked');
  };

  const field = (entry: PoiField, group?: { visible: boolean }) => {
    const flags = evaluateField(entry, context, group);

    if (!flags.visible) return null;

    const why = (entry.rules ?? []).map((rule) => whenWords(rule.when, candidates, languages.resolve)).join(' · ');

    return (
      <Box key={entry.id} sx={{ px: 1.25, py: 1 }}>
        <FieldInput
          field={entry}
          languages={languages}
          value={answers[entry.id ?? '']}
          onAnswer={answer}
          required={flags.required}
          readOnly={flags.readOnly}
          marker={entry.visible === false && (
            <Tooltip title={t('Customization_Designer_AskedWhen', [why])}>
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: LOGIC_COLOR, flexShrink: 0 }} />
            </Tooltip>
          )}
        />
      </Box>
    );
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ maxWidth: 640, mx: 'auto' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{t('Customization_Designer_TryHint')}</Typography>
        <Button size="small" startIcon={<RedoIcon />} sx={{ textTransform: 'none' }} onClick={reset}>
          {t('Customization_Designer_ResetAnswers')}
        </Button>
      </Stack>

      <Box sx={deviceStyles}>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', px: 1.25, mb: 1 }}>{t('Customization_Designer_BuiltIn')}</Typography>
        {asksStatus && (
          <Stack direction="row" alignItems="center" sx={{ px: 1.25, mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{t('Customization_StepStatus')}</Typography>
            <ToggleButtonGroup
              exclusive size="small" sx={segmentedStyles} value={status}
              onChange={(_event, next: Inspection.NoticeType | null) => next && setStatus(next)}
            >
              {stepStatuses.map((name) => <ToggleButton key={name} value={name}>{t(STEP_STATUS_LABELS[name])}</ToggleButton>)}
            </ToggleButtonGroup>
          </Stack>
        )}
        {items.map((item) => {
          if (item.kind === 'field') return field(item.field);

          const group = evaluateGroup(item.group, context);

          return group.visible && (
            <Box key={item.group.id} sx={{ mt: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 1.25, fontWeight: 700 }}>
                <LabelText text={item.group.name} empty={t('Customization_NewGroup')} languages={languages} />
              </Typography>
              {item.group.fields.map((entry) => field(entry, group))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default TryForm;
